import { Nav } from "rsuite";
import ResponsiveNav from "@rsuite/responsive-nav";
import { callFunctionIfExists } from "../../utils/GeneralUtils";
import { useAtomValue } from "jotai";
import { IsMobileAtom } from "../../App";
import ReorderColumns from "./ReorderColumns";

export interface AppTab {
  title: React.ReactNode;
  activeValue: string;
  hidden?: boolean;
  disabled?: boolean;
  externalLink?: string;
}
interface Props {
  tabs: AppTab[];
  activeTab: string | undefined;
  onSelect: ((val: string) => void) | React.Dispatch<React.SetStateAction<any>>;
  dynamic?: boolean;
  dynamicTitle?: string;
  isEditingOrder?: boolean;
  setIsEditingOrder?: (bool: boolean) => void;
  onAdd?: () => void;
  onReorder?: (newOrder: string[]) => void;
  disabledProp?: string;
  showDisabled?: boolean;
  fontSize?: string;
}

/**
 *
 * tabs - Expects an array of objects containing { title: "", activeValue: "" }
 * activeTab - Expects the active tab value (tab.activeValue)
 * dynamic - Expects a boolean deciding if new tabs can be added to the Navbar.
 */
const AppNavbar = ({
  tabs,
  activeTab,
  onSelect,
  dynamic = false,
  dynamicTitle = "View",
  isEditingOrder = false,
  setIsEditingOrder,
  onAdd,
  onReorder,
  showDisabled = true,
  fontSize = "md",
}: Props) => {
  const openInNewTab = (url: string) => {
    const newWindow = window.open(`${url}`, "_blank", "noopener,noreferrer");
    if (newWindow) newWindow.opener = null;
  };
  const handleSelect = (ek: string) => {
    if (ek.startsWith("exception-")) return;
    onSelect(ek);
  };

  if (!tabs) return null;

  const onDragEnd = (tabIds: string[]) => {
    callFunctionIfExists(onReorder, tabIds);
    setIsEditingOrder && setIsEditingOrder(false);
  };

  return (
    <>
      {isEditingOrder ? (
        <ReorderColumns
          columns={tabs}
          onDragEnd={onDragEnd}
          onCancel={() => setIsEditingOrder && setIsEditingOrder(false)}
          showDisabled={showDisabled}
        />
      ) : (
        <NavbarWrapper
          fontSize={fontSize}
          activeTab={activeTab || ""}
          handleSelect={handleSelect}
        >
          {tabs?.map((tab) => {
            if (tab?.hidden) return null;

            if (tab?.externalLink) {
              return (
                <ResponsiveNav.Item
                  key={tab.activeValue}
                  eventKey={`exception-external-${tab.activeValue}`}
                  onClick={(e: any) => {
                    if (e.nativeEvent.srcElement.id === "dots-option-menu")
                      return;
                    openInNewTab(tab.externalLink!);
                  }}
                  disabled={tab.disabled ? tab.disabled : false}
                >
                  {tab.title}
                </ResponsiveNav.Item>
              );
            }

            return (
              <ResponsiveNav.Item
                key={tab.activeValue}
                eventKey={tab.activeValue}
                disabled={tab.disabled ? tab.disabled : false}
              >
                {tab.title}
              </ResponsiveNav.Item>
            );
          })}
          {dynamic && (
            <ResponsiveNav.Item onClick={onAdd} eventKey="exception-addview">
              <div className="d-flex align-items-center">
                <i className="mdi mdi-plus-thick pr-1"></i>
                {dynamicTitle}
              </div>
            </ResponsiveNav.Item>
          )}
        </NavbarWrapper>
      )}
    </>
  );
};

const NavbarWrapper = ({
  children,
  activeTab,
  fontSize,
  handleSelect,
}: {
  children: React.ReactNode;
  activeTab: string | undefined;
  fontSize: string;
  handleSelect: (key: string) => void;
}) => {
  const isMobile = useAtomValue(IsMobileAtom);

  // Mobile Nav - User can scroll the Navbar to view more tabs
  if (isMobile) {
    return (
      <div className="gc-nav-slide-container">
        <Nav
          className="gc-responsive-nav"
          activeKey={activeTab}
          onSelect={(key: any, e: any): any => {
            if (e.nativeEvent.srcElement.id === "dots-option-menu") return;
            if (!key) return;

            handleSelect(key);
          }}
          appearance="subtle"
          style={{
            height: 35,
            fontSize: fontSize === "md" ? 15 : 14,
          }}
        >
          {children}
        </Nav>
      </div>
    );
  }

  // Desktop Nav - "More" button appears when tabs overflow automatically.
  return (
    <ResponsiveNav
      className="gc-responsive-nav"
      activeKey={activeTab}
      onSelect={(key: any, e: any): any => {
        if (e.nativeEvent.srcElement.id === "dots-option-menu") return;
        if (!key) return;

        handleSelect(key);
      }}
      appearance="subtle"
      style={{
        height: 35,
        fontSize: fontSize === "md" ? 15 : 14,
      }}
      moreProps={{ placement: "bottomEnd" }}
    >
      {children as React.ReactNode[]}
    </ResponsiveNav>
  );
};
export default AppNavbar;
